// React imports
import { createPortal } from 'react-dom';

// Third-party imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPen } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch } from 'react-redux';

// Types
import { Promocode } from '@spike/promocode-model';

// Actions
import { showSuccessThunk } from '@spike/notifications-action';

// Providers
import { usePromoCodes } from '../PromoCodesProvider';

// Local Components
import CustomButton from './CustomButton';

// Styles
import styles from './PromoCodeDetailModal.module.scss';

interface PromoCodeDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEditPromo: () => void;
}

const PromoCodeDetailModal = ({ isOpen, onClose, onEditPromo }: PromoCodeDetailModalProps) => {
    const dispatch = useDispatch();
    const { showUsage } = usePromoCodes();
    if (!isOpen) return null;

    const featureFlag = false;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const promoType = (promoCode: Promocode) => {
        switch (promoCode.discountType) {
            case 'percentage':
                return { type: 'Percentage Off', value: `${promoCode.discountAmount}% Off` };
            default:
                return { type: 'Amount Off', value: `$${promoCode.discountAmount} Off` };
        }
    };

    const handleCopyPromoCode = async () => {
        await navigator.clipboard.writeText(showUsage!.name);
        dispatch(showSuccessThunk('Promocode copied to clipboard'));
    };

    const CustomButtonTitle = ({ title }: { title: string }) => {
        return (
            <div className={styles.customButtonTitle}>
                <FontAwesomeIcon icon={faPen} />
                {title}
            </div>
        );
    };

    return createPortal(
        <div
            className={styles.backdrop}
            onClick={handleBackdropClick}
        >
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Promo Code Details</h2>
                    <div className={styles.headerActions}>
                        <CustomButton
                            title={<CustomButtonTitle title="Edit Promo" />}
                            onAction={onEditPromo}
                        />
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className={styles.content}>
                    {/* Basic Info Grid */}
                    <div className={styles.basicInfoGrid}>
                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>Promo Code</h3>
                                <div className={styles.codeValue}>
                                    {showUsage!.name}
                                    <span
                                        className={styles.copyIcon}
                                        onClick={handleCopyPromoCode}
                                    >
                                        <FontAwesomeIcon icon={faCopy} />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>Promo Type</h3>
                                <div className={styles.infoValue}>{promoType(showUsage!).type}</div>
                            </div>
                        </div>

                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>Value</h3>
                                <div className={styles.infoValue}>{promoType(showUsage!).value}</div>
                            </div>
                        </div>
                    </div>

                    {/* Date Grid */}
                    <div className={styles.dateGrid}>
                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>Start Date</h3>
                                <div className={styles.infoValue}>
                                    {showUsage!.startAt?.format('MMM DD, YYYY') || 'Not set'}
                                </div>
                                <div className={styles.timeValue}>
                                    {showUsage!.startAt?.format('h:mm a') || '12:00pm'}
                                </div>
                            </div>
                        </div>

                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>End Date</h3>
                                {showUsage!.endAt ? (
                                    <>
                                        <div className={styles.infoValue}>
                                            {showUsage!.endAt?.format('MMM DD, YYYY') || 'Not set'}
                                        </div>
                                        <div className={styles.timeValue}>
                                            {showUsage!.endAt?.format('h:mm a') || '12:00pm'}
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.infoValue}>Never</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Redeem Online Grid */}
                    <div className={styles.redeemOnlineGrid}>
                        <div className={styles.infoColumn}>
                            <div className={styles.infoSection}>
                                <h3 className={styles.sectionTitle}>Redeem Online</h3>
                                <div className={styles.onlineValue}>{showUsage!.olb ? 'Yes' : 'No'}</div>
                            </div>
                        </div>
                    </div>

                    {featureFlag && (
                        <>
                            <hr className={styles.divider} />

                            {/* Limits Section */}
                            <div className={styles.limitsSection}>
                                <h3 className={styles.sectionTitle}>Limits</h3>
                                <div className={styles.limitsText}>
                                    Single use per client, 10 total uses, sales from $100.00
                                </div>
                            </div>

                            <hr className={styles.divider} />

                            {/* Selected Items Section */}
                            <div className={styles.selectedItemsSection}>
                                <h3 className={styles.sectionTitle}>Selected Items</h3>

                                <div className={styles.itemCategory}>
                                    <h4 className={styles.categoryTitle}>Grooming Services</h4>
                                    <ul className={styles.itemList}>
                                        <li>Full Grooming, Bath & Brush</li>
                                        <li>Grooming Deluxe</li>
                                        <li>Bath & Brush</li>
                                    </ul>
                                </div>
                            </div>

                            <hr className={styles.divider} />

                            {/* Add-Ons Section */}
                            <div className={styles.addOnsSection}>
                                <h3 className={styles.sectionTitle}>Add-Ons</h3>
                                <div className={styles.allSelectedText}>All Add-Ons Selected</div>
                            </div>

                            <hr className={styles.divider} />

                            {/* Products Section */}
                            <div className={styles.productsSection}>
                                <h3 className={styles.sectionTitle}>Products</h3>
                                <div className={styles.allSelectedText}>All Products Selected</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PromoCodeDetailModal;
